<script setup lang="ts">
import EButton from './EButton.vue'
import EIcon from './EIcon.vue'
import type { IconName } from './icons'

/**
 * Empty states teach (CLAUDE.md §11).
 *
 * The `description` is not decoration: it is where the screen explains what the
 * thing is for and how to get the first one. "No purchase orders yet — create
 * one, or convert an approved purchase requisition" beats "No data" every time,
 * and is the difference between a new tenant finishing onboarding and not.
 *
 * Three flavours, because "nothing yet" and "nothing matched your filter" want
 * completely different next actions.
 */

withDefaults(
  defineProps<{
    title: string
    description?: string
    icon?: IconName
    /** Label of the primary action. Omit for a purely informational state. */
    actionLabel?: string
    /** Secondary action, e.g. "Clear filters" or "Import from CSV". */
    secondaryActionLabel?: string
    /** `filtered` and `error` change the tone and the implied next step. */
    variant?: 'empty' | 'filtered' | 'error'
    compact?: boolean
  }>(),
  { description: '', actionLabel: '', secondaryActionLabel: '', variant: 'empty' },
)

const emit = defineEmits<{ action: []; secondaryAction: [] }>()
</script>

<template>
  <div
    class="e-empty"
    :class="{ 'e-empty--compact': compact, 'e-empty--error': variant === 'error' }"
    role="status"
  >
    <span class="e-empty__icon" :class="`e-empty__icon--${variant}`" aria-hidden="true">
      <EIcon
        :name="icon ?? (variant === 'error' ? 'alert' : variant === 'filtered' ? 'filter' : 'inbox')"
        :size="22"
      />
    </span>

    <h3 class="e-empty__title">{{ title }}</h3>
    <p v-if="description !== '' || $slots.description" class="e-empty__description">
      <slot name="description">{{ description }}</slot>
    </p>

    <div v-if="actionLabel !== '' || secondaryActionLabel !== '' || $slots.actions" class="e-empty__actions">
      <slot name="actions">
        <EButton v-if="actionLabel !== ''" variant="primary" size="sm" @click="emit('action')">
          {{ actionLabel }}
        </EButton>
        <EButton
          v-if="secondaryActionLabel !== ''"
          variant="ghost"
          size="sm"
          @click="emit('secondaryAction')"
        >
          {{ secondaryActionLabel }}
        </EButton>
      </slot>
    </div>
  </div>
</template>

<style>
.e-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--erpx-space-2);
  padding: var(--erpx-space-12) var(--erpx-space-6);
  text-align: center;
}

.e-empty--compact {
  padding: var(--erpx-space-6) var(--erpx-space-4);
}

.e-empty__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  margin-block-end: var(--erpx-space-1);
  border-radius: var(--erpx-radius-full);
  background-color: var(--erpx-color-neutral-surface);
  color: var(--erpx-color-text-muted);
}

.e-empty__icon--error {
  background-color: var(--erpx-color-danger-surface);
  color: var(--erpx-color-danger-text);
}

.e-empty__title {
  margin: 0;
  color: var(--erpx-color-text-primary);
  font-size: var(--erpx-text-base);
  font-weight: var(--erpx-weight-semibold);
  line-height: var(--erpx-leading-tight);
}

.e-empty__description {
  max-width: 34rem;
  margin: 0;
  color: var(--erpx-color-text-muted);
  font-size: var(--erpx-text-sm);
  /* A reading surface, so the one place generous leading belongs. */
  line-height: var(--erpx-leading-reading);
}

.e-empty__actions {
  display: flex;
  align-items: center;
  gap: var(--erpx-space-2);
  margin-block-start: var(--erpx-space-2);
}
</style>
